// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { Currency } from '@/currency/api/types';
import {
  serializeCurrencyForAPI,
  serializeCurrencyForAPINullable,
} from '@/currency/utilities/serialize';
import { projectLineItemSchema } from '@/project/api/types';
import { z } from 'zod';

const updateProjectLineItemResponseSchema = z.object({
  data: projectLineItemSchema,
  metadata: responseMetadataSchema,
});

type UpdateProjectLineItemResponse = z.infer<
  typeof updateProjectLineItemResponseSchema
>;

/**
 * Wire shape of milestone phases in the update request. Entries carry a bare
 * percentage number. Build it from the form shape with `toWireMilestonePhases`.
 */
export interface MilestonePhasesRequest {
  mode: 'PERCENTAGE';
  entries: { name: string; percentage: number }[];
}

/**
 * Sparse update parameters for a single project line item.
 * Only fields that are explicitly provided will be updated.
 * Use `undefined` to skip a field, `null` to explicitly clear it.
 */
export interface ChargeRequestItem {
  id: string | null;
  label: string;
  pricingMode: string;
  quantity: number;
  unitPrice: Currency | null;
  totalPrice: Currency;
  servicePeriodStart: string | null;
  servicePeriodEnd: string | null;
  expectedBillableDate: string | null;
  status: string;
  isCustomModified: boolean;
  // MANUAL for milestone-generated charges (the cron does not auto-flip
  // them), TIME for periodic. This is the only piece of milestone
  // semantics persisted per charge — there is no per-charge link back
  // to a specific phase.
  triggerType: 'TIME' | 'MANUAL' | 'EVENT';
  // Display-order counter the BE writes to charge.sequence. The drawer
  // stamps this as arrayIndex + 1 on the payload so the local list's
  // order becomes the stored order.
  sequence: number;
}

export interface UpdateProjectLineItemParameters {
  projectKey: string;
  lineItemKey: string;
  unitPrice?: Currency | null;
  pricingMode?: string | null;
  chargeSchedule?: string | null;
  chargeFrequency?: string | null;
  invoiceFrequency?: string | null;
  invoiceMode?: string | null;
  fixedPrice?: Currency | null;
  totalOverride?: Currency | null;
  // Null explicitly clears the stored phases (used when switching back to
  // PERIODIC); the BE rejects a null value when chargeSchedule = MILESTONE.
  milestonePhases?: MilestonePhasesRequest | null;
  // Plan-level CONFIRMED / PENDING. PENDING is only valid for PERIODIC
  // schedules; the BE rejects PENDING transitions when any charge has
  // progressed past PLANNED.
  chargeMode?: 'CONFIRMED' | 'PENDING';
  // ISO date string. Required when chargeSchedule is PERIODIC; the BE's
  // CHECK enforces NOT NULL in that case. Pass null to clear when moving
  // to MILESTONE.
  activationDate?: string | null;
  charges?: ChargeRequestItem[];
}

export async function updateProjectLineItem(
  parameters: UpdateProjectLineItemParameters
): Promise<UpdateProjectLineItemResponse> {
  const url = `/v1/project/${parameters.projectKey}/line-item/${parameters.lineItemKey}`;

  // Build sparse payload — only include fields that are explicitly provided
  const payload: Record<string, unknown> = {};
  if (parameters.unitPrice !== undefined)
    payload.unitPrice = serializeCurrencyForAPINullable(parameters.unitPrice);
  if (parameters.pricingMode !== undefined)
    payload.pricingMode = parameters.pricingMode;
  if (parameters.chargeSchedule !== undefined)
    payload.chargeSchedule = parameters.chargeSchedule;
  if (parameters.chargeFrequency !== undefined)
    payload.chargeFrequency = parameters.chargeFrequency;
  if (parameters.invoiceFrequency !== undefined)
    payload.invoiceFrequency = parameters.invoiceFrequency;
  if (parameters.invoiceMode !== undefined)
    payload.invoiceMode = parameters.invoiceMode;
  if (parameters.fixedPrice !== undefined)
    payload.fixedPrice = serializeCurrencyForAPINullable(parameters.fixedPrice);
  if (parameters.totalOverride !== undefined)
    payload.totalOverride = serializeCurrencyForAPINullable(
      parameters.totalOverride
    );
  if (parameters.milestonePhases !== undefined)
    payload.milestonePhases = parameters.milestonePhases;
  if (parameters.chargeMode !== undefined)
    payload.chargeMode = parameters.chargeMode;
  if (parameters.activationDate !== undefined)
    payload.activationDate = parameters.activationDate;
  if (parameters.charges !== undefined)
    payload.charges = parameters.charges.map((charge) => ({
      ...charge,
      unitPrice: serializeCurrencyForAPINullable(charge.unitPrice),
      totalPrice: serializeCurrencyForAPI(charge.totalPrice),
    }));

  const response = await api.patch(url, payload);

  return parseResponse(updateProjectLineItemResponseSchema, response.data, {
    schemaName: 'UpdateProjectLineItem',
    endpoint: url,
  });
}
